// 06 — Care warning. The bird, her three vitals, the one reason she needs you,
// the ladder, and the one thing to do about it.
//
// The vitals are the sim's own physiology. The ladder is a placeholder until
// PLAN-04 builds it (REFOCUS §11c: warnings fire at distress lines, not at meter
// percentages), so the screen shows how many there are and never invents a count
// the state does not keep.

import { t } from '../../i18n';
import { completeChore } from '../actions';
import { playChoreDone } from '../sound';
import { bar } from '../components/bar';
import { segmented } from '../components/segmented';
import { button, div, h, link, paint } from '../dom';
import { selectBird, selectWarningView } from '../selectors';
import {
  placeholderNormalWarningCount,
  placeholderVeteranWarningCount,
} from '../placeholders';
import { seasonVeteranWarningCooldownHours } from '../../sim/season';
import type { Screen, ScreenContext } from '../router';
import { birdScreen } from './bird';

export function warningScreen(): Screen<HTMLElement> {
  return {
    id: 'warning',
    render: (ctx: ScreenContext<HTMLElement>) => {
      const { store, shell } = ctx.services;
      const chickenId = ctx.params.chicken ?? '';
      const bird = selectBird(store.getState(), chickenId);
      if (!bird) return gone(ctx);

      const view = selectWarningView(store.getState(), bird);
      // The header switch previews the other mode's ladder. It does not change
      // the mode — that is the breed screen's choice, and a Parent PIN can lock
      // it (REFOCUS §3).
      let mode: 'normal' | 'veteran' = view.ladder.veteran ? 'veteran' : 'normal';

      const head = div(
        { class: 'cf-screen__head' },
        link({
          class: 'cf-btn cf-btn--link',
          text: t('coop.backToCoop'),
          on: { click: () => ctx.router.back() },
        }),
        segmented<'normal' | 'veteran'>({
          label: t('warning.modeLabel'),
          value: mode,
          options: [
            { value: 'normal', label: t('warning.modeNormal') },
            { value: 'veteran', label: t('warning.modeVeteran') },
          ],
          onChange: (next) => {
            mode = next;
            paintAll();
          },
        }),
      );
      const body = div({ class: 'cf-stack' });
      const foot = div({ class: 'cf-screen__foot' });
      const screen = div({ class: 'cf-screen' }, head, body, foot);

      function paintAll(): void {
        const live = selectBird(store.getState(), chickenId);
        const current = live ? selectWarningView(store.getState(), live) : view;
        paint(
          body,
          `${current.birdName}|${current.vitals.map((vital) => `${vital.key}${vital.value}`).join(',')}|${mode}`,
          () => buildBody(current, mode),
        );
        paint(foot, `${current.fixKey}|${current.birdId}`, () =>
          div(
            { class: 'cf-screen__foot' },
            button({
              class: 'cf-btn cf-btn--primary',
              text: t(current.fixKey),
              on: {
                click: () => {
                  if (!current.fixChore) {
                    // Heat has no chore behind it, and that is a real gap rather
                    // than a missing button. What clears a heat-stressed bird in
                    // this sim is `env.sheltered` (sim/disease.ts), which a placed
                    // shade structure provides — so the lever is equipment, not a
                    // tap. The equipment is also not obtainable in play: the game
                    // has no store screen, and the `shade_sail` is gated behind a
                    // first heat stress as well. So the fix tap opens the bird
                    // rather than performing something the yard cannot do. Stated
                    // limitation: the heat warning is informational until a
                    // player-reachable shade action exists.
                    ctx.router.push(birdScreen(), { chicken: chickenId });
                    return;
                  }
                  const result = completeChore(store, current.fixChore);
                  if (result.performed) playChoreDone(store);
                  shell.toast(
                    result.performed
                      ? t('warning.fixed', { chore: t(`chore.${current.fixChore}`) })
                      : t(result.messageKey ?? 'chore.noSupplies', result.messageParams),
                  );
                  paintAll();
                },
              },
            }),
          ),
        );
      }

      paintAll();
      screen.tabIndex = -1;
      return screen;
    },
  };
}

function buildBody(
  view: ReturnType<typeof selectWarningView>,
  mode: 'normal' | 'veteran',
): HTMLElement {
  const total = mode === 'veteran' ? placeholderVeteranWarningCount : placeholderNormalWarningCount;
  return div(
    { class: 'cf-stack' },
    div(
      { class: 'cf-warninghead' },
      h('img', {
        class: 'cf-warninghead__img',
        attrs: { src: view.sprite, alt: t('bird.portraitAlt', { name: view.birdName }) },
      }),
      div(
        { class: 'cf-stack cf-stack--tight' },
        h('h1', { class: 'cf-display', text: t('warning.title', { name: view.birdName }) }),
        h('span', {
          class: 'cf-sub',
          text: t('warning.breedLine', { breed: view.breed, weeks: view.weeks }),
        }),
      ),
    ),
    div(
      { class: 'cf-card cf-stack' },
      ...view.vitals.map((vital) =>
        bar({ label: vital.label, value: vital.value, valueLabel: vital.valueLabel, tone: vital.tone }),
      ),
      h('p', { class: 'cf-small', text: t(view.causeKey, view.causeParams) }),
    ),
    div(
      { class: `cf-ladder${mode === 'veteran' ? ' cf-ladder--vet' : ''}` },
      div(
        { class: 'cf-ladder__head' },
        h('span', {
          class: 'cf-ladder__label',
          text: mode === 'veteran' ? t('warning.ladderVeteranTitle2') : t('warning.ladderNormalTitle2'),
        }),
        h('span', { class: 'cf-ladder__count', text: t('warning.ladderCount', { count: total }) }),
      ),
      div(
        { class: 'cf-ladder__dots', attrs: { 'aria-hidden': 'true' } },
        // PLAN-04 fills these from the bird's real warning history. Until then
        // they read as "where she would stand", never as a count she has.
        ...Array.from({ length: total }, (_, index) =>
          h('span', { class: 'cf-ladder__dot', attrs: { 'data-filled': String(index < 1) } }),
        ),
      ),
      h('p', {
        class: 'cf-small',
        text: t(mode === 'veteran' ? 'warning.ladderVetNote' : 'warning.ladderNormalNote', { count: total }),
      }),
      mode === 'veteran'
        ? h('p', {
            class: 'cf-small cf-warn',
            text: t('warning.cooldown', { hours: seasonVeteranWarningCooldownHours() }),
          })
        : null,
    ),
  );
}

function gone(ctx: ScreenContext<HTMLElement>): HTMLElement {
  const screen = div(
    { class: 'cf-screen' },
    h('h1', { class: 'cf-display', text: t('warning.gone') }),
    div(
      { class: 'cf-screen__foot' },
      button({
        class: 'cf-btn cf-btn--primary',
        text: t('common.back'),
        on: { click: () => ctx.router.back() },
      }),
    ),
  );
  screen.tabIndex = -1;
  return screen;
}
